import React, { useEffect, useRef } from 'react';
import { UiHistory } from '@vyaguta/vyaguta-icons/ui';

import { leaveTypeId } from 'constants/leave';

import Table from 'components/common/table';
import PopOver from 'components/common/popover';
import ApplyButton from 'components/common/layouts/apply-buttons';

import useModalState from 'hooks/useModalState';
import { useFetchWellBeingLeaveCredits } from 'hooks/query/useFetchWellBeingLeaveCredits';
import { handleError } from 'utils/errorHandler';

import WellBeingLeaveHistory from './components/WellBeingLeaveHistory';
import { DateInput } from 'interface/common';

interface LeaveCreditRow {
  leaveType: string;
  leaveTypeId: number;
  leaveCredits: number;
  leavesTaken: number;
  available: number;
  eligible?: number;
}

interface WellBeingCredit {
  leaveDays: number;
  createdAt: string;
  expiresOn: string;
  isExpired: boolean;
  isUsed: boolean;
}

interface LeaveCreditsProps {
  canApplyLeave?: boolean;
  loading?: boolean;
  data: LeaveCreditRow[];
  employeeId: number | string;
  resignation?: DateInput | null;
  fiscalYearId?: number;
}

interface ColumnArgs {
  userResignation?: DateInput | null;
  wellBeingLeaveCredits: WellBeingCredit[];
  isLoading: boolean;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
}

const getColumns = ({ userResignation, wellBeingLeaveCredits, isLoading, open, onOpen, onClose }: ColumnArgs) => {
  const columns = [
    {
      Header: 'Leave',
      accessor: 'leaveType',
      Cell: ({ original }: { original: LeaveCreditRow }) =>
        original.leaveTypeId === leaveTypeId.WELL_BEING ? (
          <div className="d-flex gap-x-4 align-items-center">
            {original.leaveType}
            <PopOver
              interactive
              theme="light"
              disableOnScroll={true}
              open={open}
              onRequestClose={onClose}
              position="right-end"
              html={
                <WellBeingLeaveHistory
                  wellBeingLeaveCredits={wellBeingLeaveCredits}
                  isLoading={isLoading}
                  onClose={onClose}
                />
              }
            >
              <span onMouseEnter={onOpen} className="well-being-leave__history d-flex align-items-center">
                <UiHistory size={16} />
              </span>
            </PopOver>
          </div>
        ) : (
          original.leaveType
        ),
    },
    {
      Header: 'Credits',
      accessor: 'leaveCredits',
    },
    {
      Header: 'Taken',
      accessor: 'leavesTaken',
    },
    {
      Header: 'Available',
      accessor: 'available',
    },
  ];

  if (userResignation) {
    columns.push({
      Header: 'Eligible (Last 30 days)',
      accessor: 'eligible',
    });
  }

  return columns;
};

const LeaveCredits: React.FC<LeaveCreditsProps> = ({
  canApplyLeave = false,
  loading = false,
  data,
  employeeId,
  resignation,
  fiscalYearId,
}) => {
  const { open, onOpen, onClose } = useModalState();

  const {
    data: wellBeingLeaveCredits = [] as WellBeingCredit[],
    isLoading,
    isError: isWellBeingLeaveCreditsError,
    error: wellBeingLeaveCreditsError,
    errorUpdatedAt: wellBeingLeaveCreditsErrorUpdatedAt,
  } = useFetchWellBeingLeaveCredits<WellBeingCredit[]>({
    userId: employeeId,
    fiscalYearId,
  });
  const lastHandledWellBeingLeaveCreditsErrorAtRef = useRef(0);

  useEffect(() => {
    if (
      !isWellBeingLeaveCreditsError ||
      !wellBeingLeaveCreditsError ||
      wellBeingLeaveCreditsErrorUpdatedAt <= lastHandledWellBeingLeaveCreditsErrorAtRef.current
    ) {
      return;
    }

    handleError(wellBeingLeaveCreditsError as object);
    lastHandledWellBeingLeaveCreditsErrorAtRef.current = wellBeingLeaveCreditsErrorUpdatedAt;
  }, [isWellBeingLeaveCreditsError, wellBeingLeaveCreditsError, wellBeingLeaveCreditsErrorUpdatedAt]);

  return (
    <main>
      <div className="container">
        <div className="full-scope-card mt-15">
          <div
            className={[
              'full-scope-card__header',
              'table-header',
              'name-wrap',
              'd-flex',
              'justify-content-between',
              'user-select-none',
            ].join(' ')}
          >
            <div>
              <span className="table-header__title">Leave Credits</span>
            </div>

            {canApplyLeave && (
              <div className="action-bar d-flex justify-content-center">
                <ApplyButton id={employeeId} />
              </div>
            )}
          </div>

          <Table
            data={data}
            columns={getColumns({
              userResignation: resignation,
              wellBeingLeaveCredits,
              isLoading,
              open,
              onOpen,
              onClose,
            })}
            loading={loading}
            showPagination={false}
            minRows={data?.length}
            customClassName="rt-cursor-default"
          />
        </div>
      </div>
    </main>
  );
};

export default LeaveCredits;
